import Hls, { ErrorData } from 'hls.js';
import { VideoElementRenderer, VideoRendererHost } from './video-renderer';

const MAX_MEDIA_RECOVERS: number = 3;   // before rebuilding the pipeline
const MAX_NETWORK_RESUMES: number = 2;  // fatal network errors resumed before giving up
const STALL_MS: number = 5000;          // frozen this long while "playing" => act
const MAX_RECOVERIES: number = 8;       // refunded after a healthy stretch

// Recorded playback: the relay exports each segment from the camera on demand,
// so a cold fragment can take seconds to arrive.
export class HlsRenderer extends VideoElementRenderer {
  private hls: Hls | null = null;
  private mediaRecoverAttempts: number = 0;
  // Not reset by a rebuild: a dead source would otherwise loop through them.
  private networkResumes: number = 0;

  // A freeze on rapid scrubbing usually raises no error at all, so progress is
  // watched directly and recovery escalates until it moves again.
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private lastVideoTime: number = 0;
  private lastAdvanceAt: number = 0;
  private recoverStage: number = 0;
  private recoveries: number = 0;
  private lastRecoveryAt: number = 0;

  constructor(video: HTMLVideoElement, host: VideoRendererHost, private readonly playbackUrl: string) {
    super(video, host);
  }

  public start(): void {
    this.observeVideo();
    this.build();
  }

  private build(): void {
    // A rebuild keeps the position: restarting the clip from zero is worse
    // than the stall it recovers from.
    const resumeAt: number = this.hls ? this.video.currentTime : -1;
    this.teardownHls();
    this.mediaRecoverAttempts = 0;
    this.lastVideoTime = this.video.currentTime;
    this.lastAdvanceAt = Date.now();
    this.recoverStage = 0;

    // hls.js first, also on iPhone (ManagedMediaSource, iOS 17.1+): the native
    // player has none of the timeouts below for the slow on-demand segments.
    if (Hls.isSupported()) {
      const hls = new Hls({
        lowLatencyMode: false,
        backBufferLength: 90,
        // Tolerate slow on-demand exports instead of timing out into a fatal.
        fragLoadingTimeOut: 60000,
        fragLoadingMaxRetry: 6,
        fragLoadingMaxRetryTimeout: 64000,
        manifestLoadingTimeOut: 30000,
        manifestLoadingMaxRetry: 4,
        // Don't buffer far ahead, or scrubbing triggers a storm of cold exports.
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        // Small safety margin for jumping a residual buffer hole.
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 1,
        nudgeOffset: 0.2,
        nudgeMaxRetry: 10,
        startPosition: resumeAt
      });

      hls.on(Hls.Events.ERROR, (_event, data) => this.onHlsError(data));
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        this.mediaRecoverAttempts = 0;
        this.networkResumes = 0;
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => this.play());

      hls.loadSource(this.playbackUrl);
      hls.attachMedia(this.video);
      this.hls = hls;
      this.startWatchdog();
    } else if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
      // No watchdog here: nudging currentTime cancels the segment the native
      // player is waiting for, and a rebuild restarts the clip from zero.
      this.video.src = this.playbackUrl;
      this.play();
    } else {
      this.host.onFatal('HLS not supported');
    }
  }

  // One failed segment otherwise leaves hls.js in a dead fatal state.
  private onHlsError(data: ErrorData): void {
    if (!this.hls) {
      return;
    }

    // The relay answers 404 only when the session is gone (token expired, relay
    // restarted); a segment it cannot export comes back as a black one. Nothing
    // brings the session back, yet hls.js retries even a 4xx on a single
    // rendition, for minutes.
    if (data?.response?.code === 404) {
      this.host.onFatal(`network: ${data.details} 404`, true);
      return;
    }

    // hls.js couldn't nudge across a hole and often never escalates to fatal.
    if (!data?.fatal) {
      if (data?.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
        this.nudge();
      }
      return;
    }

    switch (data.type) {
      case Hls.ErrorTypes.NETWORK_ERROR:
        // Segment or manifest fetch failed or timed out, after hls.js's own
        // retries: resume loading a couple of times, then declare the source
        // dead, or a relay that stopped answering would spin behind the spinner
        // forever.
        if (this.networkResumes >= MAX_NETWORK_RESUMES) {
          this.host.onFatal(`network: ${data.details}`, true);
          return;
        }
        this.networkResumes++;
        try { this.hls.startLoad(); } catch { this.build(); }
        break;

      case Hls.ErrorTypes.MEDIA_ERROR:
        if (this.mediaRecoverAttempts < MAX_MEDIA_RECOVERS) {
          this.mediaRecoverAttempts++;
          try { this.hls.recoverMediaError(); } catch { this.build(); }
        } else {
          this.build();
        }
        break;

      default:
        this.build();
        break;
    }
  }

  private startWatchdog(): void {
    this.stopWatchdog();
    this.watchdog = setInterval(() => this.checkStall(), 1500);
  }

  private stopWatchdog(): void {
    if (this.watchdog) {
      clearInterval(this.watchdog);
      this.watchdog = null;
    }
  }

  private checkStall(): void {
    // User-paused or finished clips are not stalls.
    if (this.video.paused || this.video.ended) {
      this.lastVideoTime = this.video.currentTime;
      this.lastAdvanceAt = Date.now();
      this.recoverStage = 0;
      return;
    }

    const now: number = Date.now();

    // A seek waiting for a cold segment is the relay exporting it, not a
    // stall; hls.js's own fragment timeouts cover a relay that never answers.
    if (this.video.seeking) {
      this.lastAdvanceAt = now;
      return;
    }

    // Refund the budget after a healthy stretch, so glitches over a long
    // session don't drain it.
    if (Math.abs(this.video.currentTime - this.lastVideoTime) > 0.01) {
      this.lastVideoTime = this.video.currentTime;
      this.lastAdvanceAt = now;
      this.recoverStage = 0;
      if (this.recoveries > 0 && now - this.lastRecoveryAt > 30000) {
        this.recoveries = 0;
      }
      return;
    }

    if (now - this.lastAdvanceAt < STALL_MS) {
      return;
    }

    if (this.recoveries >= MAX_RECOVERIES) {
      return; // give up quietly; the clip stays where it is
    }

    this.recoveries++;
    this.lastRecoveryAt = now;
    this.lastAdvanceAt = now; // give each step time to take effect
    this.escalateRecovery();
  }

  // Staged recovery: cheapest fix first, hard reset last.
  private escalateRecovery(): void {
    const stage: number = this.recoverStage++;

    switch (stage) {
      case 0:
        this.nudge();                     // hop a buffer hole and resume
        break;
      case 1:
        try { this.hls?.startLoad(); } catch { /* rebuilt below on the next stage */ }
        this.nudge();                     // re-kick the loader at the current position
        break;
      case 2:
        try { this.hls?.recoverMediaError(); } catch { this.build(); }
        break;
      default:
        this.build();
        this.recoverStage = 0;
        break;
    }
  }

  // Cross a tiny gap, then resume.
  private nudge(): void {
    try {
      if (this.video.buffered.length && this.video.readyState < 3) {
        this.video.currentTime = Math.min(
          this.video.currentTime + 0.25,
          Math.max(0, this.video.duration || this.video.currentTime + 0.25)
        );
      }
    } catch {
      // Seeking on a video with no seekable range.
    }
    this.play();
  }

  private teardownHls(): void {
    this.stopWatchdog();
    if (this.hls) {
      try { this.hls.destroy(); } catch { /* already destroyed */ }
      this.hls = null;
    }
  }

  public override destroy(): void {
    this.teardownHls();
    super.destroy();
  }
}
